import React, { FC, useState } from 'react'
import styles from './StreamOverview.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import {
  streamSelector,
  streamViewTypeSelector
} from 'redux-store/stream/stream.selectors'
import { getIsDefaultThumbnail } from 'helpers/getIsDefaultThumbnail'
import { TeamVsTeamBackground } from '../Backgrounds/TeamVsTeam/TeamVsTeamBackground'
import { AccessRestrictionType, MediaObject } from 'interfaces'
import { StreamInfo } from '../StreamInfo/StreamInfo'
import { TopActionsBar } from '../TopActionsBar/TopActionsBar'
import { CTASection } from '../CTASection/CTASection'
import { RootState } from 'redux-store/store'

import { StreamCountdown } from '../StreamCountdown/StreamCountdown'
import dayjs from 'dayjs'
import { PermissionsState } from 'redux-store/permissions/permissions.interface'
import { permissionsSelector } from 'redux-store/permissions/permissions.reducer'
import { ViewType } from 'redux-store/stream/stream.interface'
import { PlayIconButton } from './PlayIconButton'
import { GeoBlockedInfo } from '../GeoBlockedInfo/GeoBlockedInfo'
import classNames from 'classnames'
import { setStreamIsLive } from 'redux-store/stream/stream.actions'
import { isViewingAsAdminSelector } from 'redux-store/roles/roles.selector'
import { getUserHasAccess } from './getUserHasAccess'
import { isStreamOfflineSelector } from 'redux-store/userInterface/userInterface.selectors'
import { StreamOfflineView } from '../StreamOfflineView/StreamOfflineView'
import { useTryOpenViewStream } from 'features/viewerPage/hooks/useTryOpenViewStream'

export const StreamOverview: FC = () => {
  const stream = useSelector(streamSelector)
  const dispatch = useDispatch()
  const isStreamOffline = useSelector(isStreamOfflineSelector)
  const tryOpenViewStream = useTryOpenViewStream()
  const now = Date.now() / 1000
  const permissions: PermissionsState = useSelector(permissionsSelector)
  const mediaObject: MediaObject = stream.data.mediaObject
  const accessRestriction = mediaObject.access_restriction
  const showGeoBlockedView = stream?.data.streamUrl?.geoBlock
  const hasDVR = stream.data.streamUrl?.urls && stream.data.streamUrl.urls.dvr
  const streamIsUpcoming = mediaObject.live_start_at > now
  const diffDays = dayjs.unix(mediaObject.live_start_at).diff(dayjs(), 'day')
  const shouldShowCountdown =
    mediaObject.type !== 'video' && !mediaObject.is_replay && streamIsUpcoming
  diffDays <= 365

  const showOnlyDays = diffDays > 3

  const [showCountdown, setShowCountdown] = useState(shouldShowCountdown)
  const hasChannelAccess = useSelector(
    (state: RootState) => state.permissions.hasChannelAccess
  )
  const isDefaultThumbnail =
    mediaObject.thumbnail && getIsDefaultThumbnail(mediaObject.thumbnail)
  const shouldShowTeamsBackgrouns = isDefaultThumbnail && mediaObject.game
  const isViewingAsAdmin = useSelector(isViewingAsAdminSelector)
  const viewType: ViewType = useSelector(streamViewTypeSelector)

  const onCountDownIsZero = () => {
    dispatch(setStreamIsLive())
    setShowCountdown(false)
  }
  const noneRestriction = accessRestriction === AccessRestrictionType.None
  const logingRequired =
    accessRestriction === AccessRestrictionType.LoginRequired

  const userHasAccess = getUserHasAccess(
    hasChannelAccess,
    permissions.canView,
    permissions.hasUserPaid,
    isViewingAsAdmin,
    noneRestriction,
    logingRequired
  )

  const showPlayButton =
    (userHasAccess || noneRestriction) &&
    [
      ViewType.REPLAY,
      ViewType.HIGHLIGHT,
      ViewType.LIVE,
      ViewType.VIDEO,
      ViewType.HAS_BEEN_BROADCASTED
    ].includes(viewType)

  if (isStreamOffline) {
    return <StreamOfflineView />
  }

  return (
    <div
      className={classNames(styles.StreamOverview, {
        [styles.HasTeamVsTeamBackgrond]: shouldShowTeamsBackgrouns
      })}
    >
      <div className={styles.Background}>
        <div className={styles.Overlay}>
          <TopActionsBar />
          <div className={styles.InnerContainer}>
            {showCountdown && (
              <div className={styles.CountdownContainer}>
                <StreamCountdown
                  streamStartAt={mediaObject.live_start_at}
                  hasReachedZero={onCountDownIsZero}
                  showOnlyDays={showOnlyDays}
                />
              </div>
            )}
            {!showCountdown && showPlayButton && !showGeoBlockedView && (
              <div className={styles.PlayIconContainer}>
                <PlayIconButton onClick={tryOpenViewStream} />
              </div>
            )}
            {showGeoBlockedView ? (
              <GeoBlockedInfo />
            ) : (
              <>
                <StreamInfo />
                <CTASection
                  userHasAccess={userHasAccess}
                  streamIsUpcoming={streamIsUpcoming}
                  streamHasDVR={hasDVR}
                  accessRestriction={accessRestriction}
                />
              </>
            )}
          </div>
        </div>
        {shouldShowTeamsBackgrouns ? (
          <div className={styles.TeamVsTeamBackground}>
            <TeamVsTeamBackground />
          </div>
        ) : (
          <img
            className={styles.BackgroundImage}
            src={mediaObject.thumbnail}
            alt={`thumbnail for ${mediaObject.title}`}
            data-testid="thumbnail-background"
          />
        )}
      </div>
    </div>
  )
}
